import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/shadcn/accordion';
import diagnosticTest from '../media/LandingPageNew/howItWorksV2/diagnosticTest.png';
import coreSkills from '../media/LandingPageNew/howItWorksV2/coreSkills.mp4';
import homeworkPractice from '../media/LandingPageNew/howItWorksV2/homeworkPractice.mp4';
import monthlyReports from '../media/LandingPageNew/howItWorksV2/monthlyReports.mp4';
import progressDashboard from '../media/LandingPageNew/howItWorksV2/progressDashboard.mp4';
import liveClass from '../media/LandingPageNew/howItWorksV2/liveClass.mp4';

const STEPS = [
  {
    key: 'diagnostic',
    title: 'Quick Diagnostic Test',
    description:
      'A short, friendly assessment checks your child’s current understanding and confidence, so we place them in exactly the right lesson from day one.',
    media: { type: 'image', src: diagnosticTest, alt: 'Diagnostic test question screen' },
  },
  {
    key: 'live-classes',
    title: 'Live Classes',
    description:
      '1-1 or small group classes led live by a real tutor. Addy leads the interactive lesson while the tutor watches the children and jumps in the moment someone is stuck.',
    media: { type: 'video', src: liveClass },
  },
  {
    key: 'homework',
    title: 'Homework Practice',
    description:
      'After every class, weekly homework is set automatically and auto-marked, so what they learned actually sticks.',
    media: { type: 'video', src: homeworkPractice },
  },
  {
    key: 'home-learning',
    title: 'Core Skills Practice',
    description:
      'Every 48 hours, your child unlocks a new core skills session to strengthen the fundamental maths skills they need, all at their own pace.',
    media: { type: 'video', src: coreSkills },
  },
  {
    key: 'progress',
    title: 'Progress Reporting',
    description:
      'Know exactly how your child is progressing with clear, easy-to-understand updates throughout their learning journey.',
    subItems: [
      {
        key: 'reports',
        title: 'Weekly Snapshot & Monthly Reports',
        description:
          "Receive a weekly summary of your child's learning, plus a detailed monthly report highlighting progress, strengths and the topics that need more practice.",
        media: { type: 'video', src: monthlyReports },
      },
      {
        key: 'dashboard',
        title: 'Progress Dashboard',
        description:
          "Log in anytime to see your child's progress across every maths topic, including scores, performance over time and areas that need more practice.",
        media: { type: 'video', src: progressDashboard },
      },
    ],
  },
];

// Renders the active step's still image or silent looping video. Keyed by src
// so switching steps remounts the element and videos restart from the top.
const StepMedia = ({ media, className }) =>
  media.type === 'video' ? (
    <video
      src={media.src}
      autoPlay
      loop
      muted
      playsInline
      className={className}
    />
  ) : (
    <img src={media.src} alt={media.alt || ''} className={className} />
  );

const HowItWorksInteractive = () => {
  const [activeKey, setActiveKey] = useState(STEPS[0].key);
  // Only the Progress Reporting step has sub-items; remembers the last
  // selected sub-bullet so reopening the step restores it.
  const [activeSubKey, setActiveSubKey] = useState('reports');

  const activeStep = STEPS.find((step) => step.key === activeKey);
  const activeMedia = activeStep.subItems
    ? (
        activeStep.subItems.find((sub) => sub.key === activeSubKey) ||
        activeStep.subItems[0]
      ).media
    : activeStep.media;

  const renderMedia = (media) => (
    <AnimatePresence mode="wait">
      <motion.div
        key={media.src}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="w-full h-full flex items-center justify-center"
      >
        <StepMedia media={media} className="max-w-full max-h-full object-contain rounded-2xl" />
      </motion.div>
    </AnimatePresence>
  );

  const renderSubItems = (step) => (
    <div className="flex flex-col gap-2 mt-3">
      {step.subItems.map((sub) => {
        const isActive = sub.key === activeSubKey;
        return (
          <button
            key={sub.key}
            type="button"
            onClick={() => setActiveSubKey(sub.key)}
            className={`text-left rounded-xl px-4 py-3 border transition-all ${
              isActive
                ? 'border-[#B2FA18] bg-[#B2FA18]/15 hover:bg-[#B2FA18]/25'
                : 'border-gray-200 hover:border-[#B2FA18] hover:bg-[#B2FA18]/10 hover:shadow-sm'
            }`}
          >
            <span
              className="block text-[#2F1E6B] text-base font-medium"
              style={{ fontFamily: 'Fredoka, sans-serif' }}
            >
              {sub.title}
            </span>
            {isActive && (
              <span
                className="block text-black text-sm mt-1 leading-relaxed"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {sub.description}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-start max-w-7xl mx-auto">
      {/* Left column: step headings */}
      <div className="w-full lg:w-1/2">
        <Accordion
          type="single"
          value={activeKey}
          onValueChange={(value) => value && setActiveKey(value)}
          className="w-full"
        >
          {STEPS.map((step, index) => (
            <AccordionItem key={step.key} value={step.key} className="border-b border-[#E5E0F5]">
              <AccordionTrigger
                className="text-left hover:no-underline py-5 gap-4 px-3 -mx-3 rounded-xl text-lg lg:text-xl data-[state=open]:text-[#2F1E6B] text-black transition-colors hover:bg-[#F0EDFA] hover:text-[#2F1E6B] [&>svg]:h-6 [&>svg]:w-6 [&>svg]:stroke-[2.5] [&>svg]:text-[#2F1E6B]"
                style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: 500 }}
              >
                <span className="flex items-center gap-4">
                  <span
                    className={`flex items-center justify-center shrink-0 w-9 h-9 rounded-full text-base ${
                      step.key === activeKey
                        ? 'bg-[#B2FA18] text-[#2F1E6B]'
                        : 'bg-[#F0EDFA] text-[#2F1E6B]'
                    }`}
                  >
                    {index + 1}
                  </span>
                  {step.title}
                </span>
              </AccordionTrigger>
              <AccordionContent className="pl-[52px] pr-2">
                <p
                  className="text-black text-sm lg:text-base leading-relaxed"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  {step.description}
                </p>
                {step.subItems && renderSubItems(step)}
                {/* On mobile the media shows inline under the open step */}
                <div className="lg:hidden mt-4 rounded-2xl bg-[#F0EDFA] p-3 h-64 sm:h-80">
                  {renderMedia(
                    step.subItems
                      ? (
                          step.subItems.find((sub) => sub.key === activeSubKey) ||
                          step.subItems[0]
                        ).media
                      : step.media
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>

      {/* Right column: media panel (desktop only) */}
      <div className="hidden lg:block w-full lg:w-1/2 lg:sticky lg:top-24">
        <div className="rounded-3xl bg-[#F0EDFA] p-4 h-[480px] xl:h-[540px]">
          {renderMedia(activeMedia)}
        </div>
      </div>
    </div>
  );
};

export default HowItWorksInteractive;
